// src/pages/managerUtils.js
// ===========================================================================
// Shared helpers for manager records.
//
// MANAGEMENT COMPANIES keep their managers in numbered slots, exactly as before:
//   managerName1 / managerEmail1 / managerPhone1 / managerTitle1   (NEW: title)
//   managerName2 / ...
// The numbered layout is kept so nothing else that reads those fields breaks.
//
// PROPERTIES now carry a `managers` array:
//   managers: [{ name, title, email, phone }, ...]
// The FIRST entry is the primary manager and is mirrored into the old flat
// fields (managerName / managerEmail / managerPhone / managerTitle). Cloud
// Functions (proposal, report and completion emails) read property.managerEmail,
// so they keep working untouched and send to the primary manager.
// ===========================================================================

export const MANAGER_TITLES = ["Manager", "Assistant Manager", "Maintenance Manager"];

const SLOT_KEY = /^manager(Name|Email|Phone|Title)(\d+)$/;

const clean = (value) => String(value ?? "").trim();

/** Highest numbered manager slot present on a company doc (0 if none). */
export function companyManagerSlotCount(company) {
  if (!company) return 0;
  let max = 0;
  Object.keys(company).forEach((key) => {
    const match = key.match(SLOT_KEY);
    if (match) max = Math.max(max, Number(match[2]));
  });
  return max;
}

/** Numbered slots -> [{ name, title, email, phone }]. Fully empty slots are skipped. */
export function readCompanyManagers(company) {
  const count = companyManagerSlotCount(company);
  const list = [];
  for (let i = 1; i <= count; i += 1) {
    const manager = {
      name: clean(company[`managerName${i}`]),
      title: clean(company[`managerTitle${i}`]),
      email: clean(company[`managerEmail${i}`]),
      phone: clean(company[`managerPhone${i}`]),
    };
    if (manager.name || manager.email || manager.phone) list.push(manager);
  }
  return list;
}

/** [{...}] -> { managerName1, managerTitle1, managerEmail1, managerPhone1, ... } */
export function buildCompanyManagerFields(managers, startSlot = 1) {
  const fields = {};
  managers.forEach((m, index) => {
    const slot = startSlot + index;
    fields[`managerName${slot}`] = clean(m.name);
    fields[`managerTitle${slot}`] = clean(m.title);
    fields[`managerEmail${slot}`] = clean(m.email);
    fields[`managerPhone${slot}`] = clean(m.phone);
  });
  return fields;
}

/** Copy of a company object with every numbered manager field removed. */
export function stripCompanyManagerFields(company) {
  const out = {};
  Object.keys(company || {}).forEach((key) => {
    if (!SLOT_KEY.test(key)) out[key] = company[key];
  });
  return out;
}

/** Drops rows with no name, trims everything, removes UI-only flags. */
export function cleanManagerList(managers) {
  return (managers || [])
    .map((m) => ({
      name: clean(m.name),
      title: clean(m.title),
      email: clean(m.email),
      phone: clean(m.phone),
    }))
    .filter((m) => m.name);
}

export function findCompanyManager(company, name) {
  const target = clean(name).toLowerCase();
  if (!target) return null;
  return readCompanyManagers(company).find((m) => m.name.toLowerCase() === target) || null;
}

/**
 * A property's managers. Uses the new `managers` array when present; otherwise
 * builds a one-item list from the legacy flat fields so older properties show up.
 * When the company is passed, titles are refreshed from the company record
 * (the company is the source of truth for a manager's title).
 */
export function readPropertyManagers(property, company = null) {
  if (!property) return [];

  let list;
  if (Array.isArray(property.managers) && property.managers.length > 0) {
    list = cleanManagerList(property.managers);
  } else if (clean(property.managerName)) {
    list = cleanManagerList([
      {
        name: property.managerName,
        title: property.managerTitle,
        email: property.managerEmail,
        phone: property.managerPhone,
      },
    ]);
  } else {
    list = [];
  }

  if (!company) return list;

  return list.map((m) => {
    const match = findCompanyManager(company, m.name);
    return match ? { ...m, title: match.title || m.title } : m;
  });
}

/** Flat legacy fields mirrored from the primary (first) manager. */
export function primaryManagerFields(managers) {
  const first = cleanManagerList(managers)[0] || {};
  return {
    managerName: first.name || "",
    managerTitle: first.title || "",
    managerEmail: first.email || "",
    managerPhone: first.phone || "",
  };
}

/** Which manager to auto-pick when a company is chosen: first titled "Manager", else first A-Z. */
export function defaultManagerFor(company) {
  const list = readCompanyManagers(company).sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
  );
  return list.find((m) => m.title === "Manager") || list[0] || null;
}
